import React, { useState, useEffect } from 'react';
import { inject, observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import * as routes from 'routes';

import Button from '@material-ui/core/Button';
import { MTableToolbar } from '@material-table/core';
import IconButton from '@material-ui/core/IconButton';
import useStyles, { centerCellStyles } from './styles/Accounts';
import Grid from '@material-ui/core/Grid';
import GetAppIcon from '@material-ui/icons/GetApp';
import RefreshIcon from '@material-ui/icons/Refresh';
import RotateLeftIcon from '@material-ui/icons/RotateLeft';

import FilterIcon from 'components/Icons/FilterIcon';

import { compareCzechDates, formatAmountValue, searchInStrings, compareAmountValues } from 'helpers/helpers';
import MaterialTable from 'components/Table/Table';
import Tooltip from 'components/Tooltip/Tooltip';
import LoadingIndicator from 'components/Loading/LoadingIndicator';

import { AccountPageTypes, AccountsStore } from 'stores/accountsStore';
import { RootStore } from 'stores/rootStore';
import GenerationDialog from 'components/GenerationDialog/GenerationDialog';
import { EdwardColors, Order } from 'enums';

interface Props {
  newAccountsStore?: AccountsStore;
  rootStore?: RootStore;
}

const Inactive: React.FC<Props> = ({ newAccountsStore, rootStore }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const history = useHistory();
  const {
    inactive,
    downloadAccounts,
    pageSize,
    onChangeRowsPerPage,
    accountsState,
    onTableSearchChange,
    onTableOrderChange,
    sortColumns,
    reloadAccounts,
    reloadingInactive,
  } = newAccountsStore!;
  const [filtersVisible, setFilterVisible] = useState(false);
  const [defaultView, setDefaultView] = useState(!newAccountsStore?.accountsState[AccountPageTypes.INACTIVE].cols);

  const state = accountsState[AccountPageTypes.INACTIVE];

  useEffect(() => {
    rootStore!.stores.uiStore.setAccountsListData({ newAccountsStore, tab: AccountPageTypes.INACTIVE });
    return () => {
      rootStore!.stores.uiStore.unsetAccountsListData();
    };
  });

  const userModified = newAccountsStore?.accountsState[AccountPageTypes.INACTIVE].userModified;

  useEffect(() => {
    setDefaultView(!newAccountsStore?.accountsState[AccountPageTypes.INACTIVE].userModified);
  }, [newAccountsStore?.accountsState, userModified]);

  const toggleFilterVisibility = () => {
    setFilterVisible(!filtersVisible);
  };

  if (!inactive) {
    return (
      <div className={classes.loadingContainer}>
        <LoadingIndicator size={56} />
      </div>
    );
  }

  return (
    <>
      <MaterialTable
        enhanced
        data={inactive}
        components={{
          Toolbar: (props) => (
            <Grid container>
              <MTableToolbar {...props} />
              <div>
                <Tooltip title={t('general_resetTableCols') || ''} placement="top">
                  <div>
                    <IconButton
                      size="small"
                      onClick={() => {
                        newAccountsStore && newAccountsStore.resetColumns(AccountPageTypes.INACTIVE);
                        setDefaultView(true);
                      }}
                    >
                      <RotateLeftIcon fontSize="large" />
                    </IconButton>
                  </div>
                </Tooltip>
                <Tooltip title={t('general_show_filters') || ''} placement="top">
                  <div>
                    <IconButton size="small" onClick={toggleFilterVisibility}>
                      <FilterIcon transparentBgr defaultFill fontSize="large" strokeWidth={0.1} />
                    </IconButton>
                  </div>
                </Tooltip>
                <Tooltip title={t('accounts_reload') || ''} placement="top">
                  <div>
                    <IconButton size="small" onClick={() => reloadAccounts('INACTIVE')}>
                        {reloadingInactive ? <LoadingIndicator /> : <RefreshIcon fontSize="large" />}
                    </IconButton>
                  </div>
                </Tooltip>
                <Button variant="outlined" onClick={downloadAccounts} startIcon={<GetAppIcon />}>
                  {t('accounts_download_csv_short')}
                </Button>
              </div>
            </Grid>
          ),
        }}
        columns={sortColumns(
          defaultView,
          AccountPageTypes.INACTIVE,
          [
            {
              title: t('general_account'),
              field: 'clientName',
              hiddenByColumnsButton: true,
              cellStyle: { width: '25%' },
              render: ({ clientName, accountName }) => (
                <>
                  <span className={classes.name}>{clientName}</span>
                  <span className={classes.secondLine}>({accountName || t('accounts_withoutName')})</span>
                </>
              ),
              customFilterAndSearch: (filter, rowData, def) =>
                searchInStrings(filter, [rowData.clientName, rowData.accountName]),
              customSort: (a, b) => {
                return `${a.clientName} ${a.accountName}`.localeCompare(`${b.clientName} ${b.accountName}`);
              },
            },
            {
              hiddenByColumnsButton: true,
              title: t('accounts_status'),
              field: 'activeStatus',
              cellStyle: { ...centerCellStyles },
              headerStyle: { textAlign: 'center' },
              customFilterAndSearch: (search, data) => {
                return (
                  t(`options_activeStatus_${data.activeStatus}`).toLowerCase().indexOf(search.trim().toLowerCase()) > -1
                );
              },
              render: ({ activeStatus }) => (
                <Tooltip title={t(`tooltip_activeStatus_${activeStatus || ''}`) || ''} placement="top">
                  <span className={`status_${activeStatus}`} style={{ color: EdwardColors.SECONDARY }}>
                    {t(`options_activeStatus_${activeStatus}`)}
                  </span>
                </Tooltip>
              ),
              customSort: (a, b) => {
                return t(`options_activeStatus_${a.activeStatus}`).localeCompare(
                  t(`options_activeStatus_${b.activeStatus}`),
                );
              },
            },
            {
              title: t('accounts_lastPayment'),
              field: 'lastPaymentDate',
              cellStyle: { ...centerCellStyles },
              headerStyle: { textAlign: 'center' },
              customSort: (a, b) => compareCzechDates(a.lastPaymentDate, b.lastPaymentDate),
              customFilterAndSearch: (filter, rowData, def) =>
                searchInStrings(filter, [rowData.lastPaymentDate, rowData.lastPaymentValue]),
              render: ({ lastPaymentDate, lastPaymentValue, currency }) => (
                <>
                  {lastPaymentDate || '—'}
                  <span className={classes.secondLine}>{formatAmountValue(lastPaymentValue, currency)}</span>
                </>
              ),
            },
            {
              title: t('accounts_totalAum'),
              field: 'totalAum',
              cellStyle: { ...centerCellStyles },
              headerStyle: { textAlign: 'center' },
              render: ({ totalAum, currency }) => formatAmountValue(totalAum, currency),
              customSort: (a, b) =>
                compareAmountValues(
                  { value: a.totalAum, currency: a.currency },
                  { value: b.totalAum, currency: b.currency },
                  newAccountsStore?.summary?.conversionRate,
                ),
            },
            {
              title: t('accounts_canTerminateContract'),
              field: 'canTerminateContract',
              cellStyle: { ...centerCellStyles },
              headerStyle: { textAlign: 'center' },
              render: ({ canTerminateContract }) => {
                return canTerminateContract ? <strong>{t('general_yes')}</strong> : t('general_no');
              },
            },
          ],
          Order.DESC,
          4,
        )}
        options={{
          searchFieldAlignment: 'left',
          headerStyle: {
            borderBottom: '2px solid rgb(224, 224, 224)',
          },
          pageSize,
          filtering: filtersVisible,
          sorting: true,
          columnsButton: true,
          searchText: state.search,
        }}
        onRowClick={(_event, { accountId }) => {
          history.push(routes.ACCOUNTS_DETAIL(accountId));
        }}
        onSearchChange={(search) => {
          onTableSearchChange(AccountPageTypes.INACTIVE, search);
        }}
        onOrderChange={(orderedColumnId, orderDirection) =>
          onTableOrderChange(AccountPageTypes.INACTIVE, orderedColumnId, orderDirection as Order)
        }
        onRowsPerPageChange={onChangeRowsPerPage}
      />

      <GenerationDialog store={newAccountsStore!} />
    </>
  );
};

export default inject('newAccountsStore', 'rootStore')(observer(Inactive));
